import React, { useEffect, useState } from 'react';
import { Card } from 'antd';
import {
  PlusOutlined,
  EditOutlined,
  CheckCircleOutlined,
  ClockCircleOutlined,
} from '@ant-design/icons';
import httpInjectorService from '../../../../services/http-injector.service';
import { useParams } from 'react-router-dom';
import Chart from 'react-apexcharts';
import { toast } from 'react-toastify';
import FilterDropdown from './FilterDrodown';

const SummaryDashboard = () => {
  const { id: projectId } = useParams();

  /* 🔹 SINGLE STATE */
  const [dashboardData, setDashboardData] = useState(null);

  const [reqBody, setReqBody] = useState({
    project_id: projectId,
    assignee_id: [],
    sprint_id: [],
    epic_id: null,
    status: [],
    priority: [],
    work_type: [],
  });

  /* 🔹 FETCH SINGLE API */
  useEffect(() => {
    fetchSummaryDashboardData();
  }, [projectId, reqBody]);

  const fetchSummaryDashboardData = async () => {
    try {
      const response = await httpInjectorService.getSummaryDashboard(reqBody);
      if (response.status === 'success') {
        setDashboardData(response.data);
      } else {
        toast.error('Failed to fetch dashboard data');
      }
    } catch (error) {
      console.error(error);
      toast.error('Something went wrong');
    }
  };

  /* 🔹 LOADING GUARD */
  if (!dashboardData) {
    return <div style={{ padding: 20 }}>Loading dashboard...</div>;
  }

  /* 🔹 DATA EXTRACTION */
  const summaryCounts = dashboardData;
  const statusData = dashboardData.status_overview_summary || {};
  const priorityData = dashboardData.priority_summary || {};
  const typesOfWorkData = dashboardData.types_of_work_summary || {};
  const epicProgressData = dashboardData.epic_progress_summary || [];
  const workloadData = dashboardData.team_workload_summary || [];
  // 🔹 FILTER VALID WORKLOAD USERS (exclude zero tasks & null names)
const validWorkloadData = workloadData.filter(
  (item) => item.task_count > 0 && item.user_name
);


  /* ---------------- STATUS DONUT ---------------- */
  const statusOptions = {
    labels: Object.keys(statusData),
    legend: { position: 'bottom' },
    title: { text: 'Task Status Overview', align: 'center' },
  };
  const statusSeries = Object.values(statusData);

  /* ---------------- PRIORITY BAR ---------------- */
  const priorityOptions = {
    chart: { type: 'bar' },
    plotOptions: { bar: { columnWidth: '50%' } },
    dataLabels: { enabled: true },
    xaxis: { categories: Object.keys(priorityData) },
    title: { text: 'Priority Overview', align: 'center' },
  };
  const prioritySeries = [
    {
      name: 'Tasks',
      data: Object.values(priorityData).map(Number),
    },
  ];

  /* ---------------- TYPES OF WORK ---------------- */
  const totalWorkItems = typesOfWorkData.Total_work_item || 1;
  const typesLabels = Object.keys(typesOfWorkData).filter(
    (k) => k !== 'Total_work_item',
  );

  const typesSeries = [
    {
      name: 'Work Items',
      data: typesLabels.map((k) =>
        ((typesOfWorkData[k] / totalWorkItems) * 100).toFixed(2),
      ),
    },
  ];

  const typesOptions = {
    chart: { type: 'bar' },
    plotOptions: { bar: { horizontal: true } },
    dataLabels: {
      enabled: true,
      formatter: (val) => `${val}%`,
    },
    xaxis: { categories: typesLabels },
    tooltip: {
      y: {
        formatter: (_, { dataPointIndex }) => {
          const label = typesLabels[dataPointIndex];
          return `${typesOfWorkData[label] || 0} / ${totalWorkItems}`;
        },
      },
    },
    title: { text: 'Types of Work (%)', align: 'center' },
  };

  /* ---------------- EPIC PROGRESS ---------------- */
  const epicCategories = epicProgressData.map((e) => e.epic_key);
  const epicTotals = epicProgressData.map((e) => e.task_status.Total || 1);

  const epicSeries = [
    {
      name: 'TO DO',
      data: epicProgressData.map((e, i) =>
        (((e.task_status['TO DO'] || 0) / epicTotals[i]) * 100).toFixed(2),
      ),
    },
    {
      name: 'IN PROGRESS',
      data: epicProgressData.map((e, i) =>
        (((e.task_status['IN PROGRESS'] || 0) / epicTotals[i]) * 100).toFixed(
          2,
        ),
      ),
    },
    {
      name: 'DONE',
      data: epicProgressData.map((e, i) =>
        (((e.task_status['DONE'] || 0) / epicTotals[i]) * 100).toFixed(2),
      ),
    },
  ];

  const epicOptions = {
    chart: { type: 'bar', stacked: true },
    plotOptions: { bar: { horizontal: true } },
    xaxis: { categories: epicCategories, max: 100 },
    dataLabels: {
      enabled: true,
      formatter: (val) => `${val}%`,
    },
    legend: { position: 'bottom' },
    title: { text: 'Epic Progress Overview (%)', align: 'center' },
  };

  /* ---------------- WORKLOAD ---------------- */
// 🔹 TOTAL TASKS (only from valid users)
const totalTasks =
  validWorkloadData.reduce((sum, item) => sum + item.task_count, 0) || 1;


  const workLoadOptions = {
    chart: { type: 'bar' },
    plotOptions: { bar: { horizontal: true } },
    dataLabels: {
      enabled: true,
      formatter: (val) => `${val}%`,
    },
   xaxis: {
  categories: validWorkloadData.map(
    (item) => item.user_name || 'Unknown'
  ),
},

    title: { text: 'Team Task Distribution (%)', align: 'center' },
  };

// 🔹 WORKLOAD SERIES (percentage, numeric)
const workLoadSeries = [
  {
    name: 'Tasks',
    data: validWorkloadData.map((item) =>
      Number(((item.task_count / totalTasks) * 100).toFixed(2))
    ),
  },
];


  return (
    <Card
      style={{
        position: 'fixed',
        top: '170px',
        left: '50px',
        right: 0,
        bottom: 0,
        display: 'flex',
        flexDirection: 'column',
        overflowY: 'auto',
      }}
    >
      <div   style={{ display: 'flex', justifyContent: 'flex-start', marginLeft: 50 }}>
        <div data-tour="summary-filter">
      <FilterDropdown
        reqBody={reqBody}
        setReqBody={setReqBody}
        dashboardData={dashboardData}
        setDashboardData={setDashboardData}
       
      />
      </div>
      </div>

      {/* SUMMARY CARDS */}
      <div
        style={{
          display: 'flex',
          justifyContent: 'center',
          gap: '50px',
          marginBottom: '4px',
        }}
        data-tour="summary-cards"
      >
        <Card
          size="small"
          style={{
            flex: '0 0 20%',
            boxShadow: '0 2px 8px rgba(0,0,0,0.12)',
          }}
          bodyStyle={{ padding: '10px 12px' }}
        >
          <div style={{ display: 'flex', alignItems: 'center' }}>
            <PlusOutlined
              style={{ fontSize: '16px', color: '#1890ff', marginRight: '8px' }}
            />
            <div>
              <h5 style={{ margin: 0, fontSize: '15px' }}>
                Created {summaryCounts.created || 0}
              </h5>
              <small style={{ color: '#888', fontSize: '11px' }}>
                past 7 days
              </small>
            </div>
          </div>
        </Card>

        <Card
          size="small"
          style={{
            flex: '0 0 20%',
            boxShadow: '0 2px 8px rgba(0,0,0,0.12)',
          }}
          bodyStyle={{ padding: '10px 12px' }}
        >
          <div style={{ display: 'flex', alignItems: 'center' }}>
            <EditOutlined
              style={{ fontSize: '16px', color: '#faad14', marginRight: '8px' }}
            />
            <div>
              <h5 style={{ margin: 0, fontSize: '15px' }}>
                Updated {summaryCounts.updated || 0}
              </h5>
              <small style={{ color: '#888', fontSize: '11px' }}>
                past 7 days
              </small>
            </div>
          </div>
        </Card>

        <Card
          size="small"
          style={{
            flex: '0 0 20%',
            boxShadow: '0 2px 8px rgba(0,0,0,0.12)',
          }}
          bodyStyle={{ padding: '10px 12px' }}
        >
          <div style={{ display: 'flex', alignItems: 'center' }}>
            <CheckCircleOutlined
              style={{ fontSize: '16px', color: '#52c41a', marginRight: '8px' }}
            />
            <div>
              <h5 style={{ margin: 0, fontSize: '15px' }}>
                Completed {summaryCounts.completed || 0}
              </h5>
              <small style={{ color: '#888', fontSize: '11px' }}>
                past 7 days
              </small>
            </div>
          </div>
        </Card>

        <Card
          size="small"
          style={{
            flex: '0 0 20%',
            boxShadow: '0 2px 8px rgba(0,0,0,0.12)',
          }}
          bodyStyle={{ padding: '10px 12px' }}
        >
          <div style={{ display: 'flex', alignItems: 'center' }}>
            <ClockCircleOutlined
              style={{ fontSize: '16px', color: '#eb2f96', marginRight: '8px' }}
            />
            <div>
              <h5 style={{ margin: 0, fontSize: '15px' }}>
                Due Soon {summaryCounts.due_soon || 0}
              </h5>
              <small style={{ color: '#888', fontSize: '11px' }}>
                past 7 days
              </small>
            </div>
          </div>
        </Card>
      </div>

      {/* CHARTS */}
      <div style={{ maxWidth: '100%', padding: '30px' }} >
        <div
          style={{
            display: 'flex',
            justifyContent: 'space-around',
            marginTop: '40px',
            maxWidth: '100%',
          }}
        >
          <Card
            style={{
              boxShadow: '0 2px 8px rgba(0,0,0,0.12)',
              height: '400px',
              width: '580px',
            }}
            data-tour="status-chart"
          >
            <Chart
              options={statusOptions}
              series={statusSeries}
              type="donut"
              width={380}
            />
          </Card>
          <Card
            style={{
              boxShadow: '0 2px 8px rgba(0,0,0,0.12)',
              height: '400px',
              width: '580px',
            }}
            data-tour="priority-chart"
          >
            <Chart
              options={priorityOptions}
              series={prioritySeries}
              type="bar"
              width={450}
            />
          </Card>
        </div>

        <div
          style={{
            display: 'flex',
            justifyContent: 'space-around',
            marginTop: 50,
            maxWidth: '100%',
          }}
        >
          <Card
            style={{
              boxShadow: '0 2px 8px rgba(0,0,0,0.12)',
              height: '400px',
              width: '580px',
            }}
            data-tour="worktype-chart"
          >
            <Chart
              options={typesOptions}
              series={typesSeries}
              type="bar"
              width={440}
            />
          </Card>
          <Card
            style={{
              boxShadow: '0 2px 8px rgba(0,0,0,0.12)',
              height: '400px',
              width: '580px',
            }}
            data-tour="epic-progress"
          >
            <Chart
              options={epicOptions}
              series={epicSeries}
              type="bar"
              width={520}
            />
          </Card>
        </div>

        <div
          style={{
            display: 'flex',
            justifyContent: 'space-around',
            marginTop: 50,
            maxWidth: '100%',
          }}
          
        >
          <Card
            style={{
              boxShadow: '0 2px 8px rgba(0,0,0,0.12)',
              height: '400px',
              width: '580px',
            }}
            data-tour="team-distribution"
          >
            <Chart
              options={workLoadOptions}
              series={workLoadSeries}
              type="bar"
              width={450}
            />
          </Card>
          <div style={{
              // boxShadow: '0 2px 8px rgba(0,0,0,0.12)',
              height: '400px',
              width: '580px',
            }}>
            
          </div>
        </div>
      </div>
    </Card>
  );
};

export default SummaryDashboard;
